import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import SideBar from "../../components/Sidebar";
import "../../../node_modules/bootstrap/dist/css/bootstrap.min.css";
import "../../../node_modules/bootstrap/dist/js/bootstrap.min.js";
import { ASK_INFO_BOT } from "../../utils/constants";
import BouncingDotsLoader from "../../components/LoadingDots";
import Chat, { ChatMessage } from "../../components/Chat";
import ChatComposer from "../../components/ChatComposer";
import useWindowWidth from "../../utils/windowWidth";

function InfoAccess() {
  const navigate = useNavigate();

  const mainRef = useRef<HTMLHeadingElement>(null);
  const location = useLocation();

  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [prompt, setPrompt] = useState("");
  const [sendDisabled, setSendDisabled] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [uuid] = useState(() => crypto.randomUUID());

  const inputRef = useRef<HTMLTextAreaElement>(null);

  function handlePromptSubmit(): void {
    const messageText = prompt.trim();
    if (!messageText || sendDisabled) return;
    setSendDisabled(true);
    setChat((chat) => [
      ...chat,
      {
        text: messageText,
        timestamp: new Date().toISOString(),
        sender: "user",
      },
    ]);
    sendChatMessage(messageText);
    setPrompt("");
  }

  /*
  const fetchChats = () => {
    const url = FETCH_INFO_CHATS;
    makeAuthenticatedGet(url)
      .then((response) => {
        console.log("chats", response);
        setChatId(response[0].sessionId);
        response[0].messages.map((msg: { text: string }) => {
          setChat((chat) => [...chat, msg.text]);
          return "";
        });
      })
      .catch((error) => {
        console.log("cannot fetch chats", error);
      });
  };
  */

  const sendChatMessage = async (messageText: string) => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(ASK_INFO_BOT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: messageText, uuid, stream: true })
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      if (!response.body) {
        throw new Error("The response did not include a message stream");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let isFirstChunk = true;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);

        if (isFirstChunk) {
          setIsStreaming(true);
          setChat((prev) => [...prev, {
            text: chunk,
            timestamp: new Date().toISOString(),
            sender: "bot",
          }]);
          isFirstChunk = false;
        } else {
          setChat((prev) => {
            const updated = [...prev];
            updated[updated.length - 1].text += chunk;
            return updated;
          });
        }
      }

    } catch (error) {
      console.error("Error fetching data:", error);
      setChat((prev) => [...prev, {
        text: "I’m sorry, I couldn’t complete that response. Please try again in a moment.",
        timestamp: new Date().toISOString(),
        sender: "bot",
      }]);
    } finally {
      setSendDisabled(false);
      setIsStreaming(false);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  useEffect(() => {
    // Auth check first
    if (
      localStorage.getItem("token") === null ||
      localStorage.getItem("token") === undefined
    ) {
      navigate("/login");
      return;
    }

    document.title = "Information Access - Nclude Portal";

    setChat([{
      text: "Hey there! I'm here to help you find information and resources. What would you like to know?",
      timestamp: new Date().toISOString(),
      sender: "bot",
    }]);
  }, [navigate]);


  const liveRegionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Set focus on the main content when the route changes
    if (mainRef.current) {
      mainRef.current.focus();
    }
  }, [location]);

  //window size
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 769;

  // if (!socket) {
  //   return <div>Loading...</div>;
  // }

  return (
    <div className="containerr">
      {!isMobile && <Header />}
      <div className="page-container">
        <SideBar activePage="Chatbot" />
        <main className="doc-container agent-chat-page" id="main-content" tabIndex={-1}>
          <div className="agent-chat-heading">
            <div>
              <p className="agent-eyebrow">Accessible information assistant</p>
              <h1 id="information-access-heading" className="display-title" ref={mainRef} tabIndex={-1}>Information Access</h1>
              <p className="agent-description">Ask questions and receive clear, accessible information and resources.</p>
            </div>
            <p className="agent-status" role="status" aria-live="polite">
              <span className={sendDisabled || isStreaming ? "status-dot busy" : "status-dot"} aria-hidden="true" />
              {sendDisabled || isStreaming ? "Responding" : "Online"}
            </p>
          </div>

          <section className="chat-box" aria-label="Information Access conversation">
            <div
              className="chat-window-container"
              role="log"
              aria-label="Information Assistant messages"
              aria-live="off"
              aria-relevant="additions text"
              aria-busy={sendDisabled}
              tabIndex={0}
            >
              {chat.length === 0 ? (
                <BouncingDotsLoader />
              ) : (
                <Chat
                  chat={chat}
                  isStreaming={isStreaming}
                  assistantName="Information Assistant"
                  announcementRef={liveRegionRef}
                />
              )}
              {sendDisabled && !isStreaming && (
                <div className="assistant-thinking" role="status">
                  <BouncingDotsLoader />
                  <span>Information Assistant is thinking…</span>
                </div>
              )}
            </div>
            <div ref={liveRegionRef} aria-live="polite" aria-atomic="true" className="visually-hidden" />
            <ChatComposer
              inputRef={inputRef}
              value={prompt}
              onChange={setPrompt}
              onSubmit={handlePromptSubmit}
              busy={sendDisabled}
              placeholder="Message the Information Assistant"
              assistantName="Information Assistant"
            />
          </section>
        </main>
      </div>
    </div>
  );
}

export default InfoAccess;
